import React from 'react';
import PropTypes from 'prop-types';
import { getNetSuiteProduct } from '../services/api';

/**
 * ProductSidebar Component
 * Displays detailed product information in a sidebar that slides in from the right
 * with a dark transparent overlay on the rest of the screen
 */
const ProductSidebar = ({ product, isOpen, onClose }) => {
  // State for NetSuite data
  const [netsuiteData, setNetsuiteData] = React.useState(null);
  const [netsuiteLoading, setNetsuiteLoading] = React.useState(false);
  const [netsuiteError, setNetsuiteError] = React.useState(null);

  // Lock body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Fetch NetSuite data when product changes
  React.useEffect(() => {
    if (!product || !product.sku) {
      setNetsuiteData(null);
      setNetsuiteError(null);
      return;
    }

    const fetchNetSuiteData = async () => {
      setNetsuiteLoading(true);
      setNetsuiteError(null);
      setNetsuiteData(null);

      try {
        const response = await getNetSuiteProduct(product.sku);
        if (response.success && response.data) {
          setNetsuiteData(response.data);
        } else {
          setNetsuiteError('No data available');
        }
      } catch (error) {
        console.error('Failed to fetch NetSuite data:', error);
        setNetsuiteError(error.message || 'Failed to load product information');
      } finally {
        setNetsuiteLoading(false);
      }
    };

    fetchNetSuiteData();
  }, [product?.sku]);

  if (!product) return null;

  const { sku, imageUrl, confidence, category, metadata } = product;

  // Styles
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? 'visible' : 'hidden',
      transition: 'opacity 0.3s ease, visibility 0.3s ease',
      cursor: 'pointer',
    },
    sidebar: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: '480px',
      maxWidth: '90vw',
      backgroundColor: '#ffffff',
      boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.2)',
      zIndex: 1000,
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease',
      overflowY: 'auto',
      cursor: 'default',
    },
    closeButton: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      width: '32px',
      height: '32px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      transition: 'background-color 0.2s ease',
      zIndex: 1,
    },
    closeIcon: {
      width: '24px',
      height: '24px',
      color: '#6b7280',
    },
    content: {
      padding: '32px',
    },
    imageSection: {
      width: '100%',
      backgroundColor: '#f9fafb',
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '32px',
      padding: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '320px',
      border: '1px solid #e5e7eb',
    },
    productImage: {
      maxWidth: '100%',
      maxHeight: '320px',
      objectFit: 'contain',
    },
    skuSection: {
      marginBottom: '32px',
    },
    skuLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '8px',
    },
    skuValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      wordBreak: 'break-word',
    },
    divider: {
      height: '1px',
      backgroundColor: '#e5e7eb',
      margin: '2px 0',
    },
    infoSection: {
      marginBottom: '2px',
    },
    infoLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#4b5563',
      marginBottom: '12px',
    },
    infoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid #f3f4f6',
    },
    infoKey: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: '500',
    },
    infoValue: {
      fontSize: '20px',
      color: '#1f2937',
      fontWeight: '600',
      textAlign: 'right',
      marginLeft: '16px',
    },
    staticSection: {
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      marginTop: '10px',
    },
    staticTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#4b5563',
      marginBottom: '12px',
      verticalAlign: 'top',
    },
    staticPlaceholder: {
      fontSize: '12px',
      color: '#9ca3af',
      lineHeight: '1.2',
    },
    loadingSpinner: {
      width: '20px',
      height: '20px',
      border: '2px solid #e5e7eb',
      borderTop: '2px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      display: 'inline-block',
      marginRight: '8px',
      verticalAlign: 'middle',
    },
    errorMessage: {
      fontSize: '13px',
      color: '#ef4444',
      lineHeight: '1.6',
    },
    netsuiteValue: {
      fontSize: '14px',
      color: '#1f2937',
      fontWeight: '500',
      textAlign: 'right',
      marginLeft: '26px',
    },
    netsuiteKey: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: '400',
    },
    netsuiteItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '5px 0',
      borderBottom: '1px solid #f3f4f6',
    },
  };

  const getConfidenceColor = (conf) => {
    if (conf >= 90) return '#10b981';
    if (conf >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const confidencePercentage = confidence?.toFixed(1) || '0';
  const confidenceColor = getConfidenceColor(parseFloat(confidencePercentage));

  return (
    <>
      {/* Dark Overlay */}
      <div
        style={styles.overlay}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        style={styles.sidebar}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          style={styles.closeButton}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <svg
            style={styles.closeIcon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content */}
        <div style={styles.content}>
          {/* Product Image */}
          <div style={styles.imageSection}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={sku}
                style={styles.productImage}
              />
            ) : (
              <div style={{ fontSize: '48px', color: '#9ca3af' }}>📷</div>
            )}
          </div>

          {/* SKU Section */}
          <div style={styles.skuSection}>
            <div style={styles.skuLabel}>Product Code</div>
            <div style={styles.skuValue}>{sku}</div>
          </div>

          <div style={styles.divider} />

          {/* Product Information */}
          <div style={styles.infoSection}>
            <div style={styles.infoItem}>
              <span style={styles.infoKey}>Confidence Score</span>
              <span
                style={{
                  ...styles.infoValue,
                  color: confidenceColor,
                }}
              >
                {confidencePercentage}%
              </span>
            </div>



            {metadata && Object.entries(metadata).map(([key, value]) => (
              <div key={key} style={styles.infoItem}>
                <span style={styles.infoKey}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </span>
                <span style={styles.infoValue}>{value}</span>
              </div>
            ))}
          </div>

          <div style={styles.divider} />

          {/* NetSuite Product Information */}
          <div style={styles.staticSection}>
            <div style={styles.staticTitle}>NetSuite Product Information</div>
            {netsuiteLoading && (
              <div style={styles.staticPlaceholder}>
                <div style={styles.loadingSpinner}></div>
                Loading product information...
              </div>
            )}
            {netsuiteError && !netsuiteLoading && (
              <div style={styles.errorMessage}>
                {netsuiteError}
              </div>
            )}
            {netsuiteData && !netsuiteLoading && (
              <div>
                {netsuiteData.description && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Description</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.description}</span>
                  </div>
                )}
                {netsuiteData.itemid && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Item ID</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.itemid}</span>
                  </div>
                )}
                {netsuiteData.barcode && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Barcode</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.barcode}</span>
                  </div>
                )}
                {netsuiteData.itemtype && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Item Type</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.itemtype}</span>
                  </div>
                )}
                {netsuiteData['class'] && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Class</span>
                    <span style={styles.netsuiteValue}>{netsuiteData['class']}</span>
                  </div>
                )}
                {netsuiteData.gender && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Gender</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.gender}</span>
                  </div>
                )}
                {netsuiteData.metal && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Metal</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.metal}</span>
                  </div>
                )}
                {netsuiteData.plating && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Plating</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.plating}</span>
                  </div>
                )}
                {netsuiteData.stone && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Stone</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.stone}</span>
                  </div>
                )}
                {netsuiteData.stone_color && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Stone Color</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.stone_color}</span>
                  </div>
                )}
                {netsuiteData.bin_location && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Bin Location</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.bin_location}</span>
                  </div>
                )}
                {netsuiteData.weight && (
                  <div style={styles.netsuiteItem}>
                    <span style={styles.netsuiteKey}>Weight</span>
                    <span style={styles.netsuiteValue}>{netsuiteData.weight}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

ProductSidebar.propTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    confidence: PropTypes.number.isRequired,
    category: PropTypes.string,
    metadata: PropTypes.object,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

ProductSidebar.defaultProps = {
  product: null,
};

export default ProductSidebar;
